import { getAutoCompleteSuggestions } from '@griply/utils';
import { useSortedTags } from 'features/tags';
import React, { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Search, Tag } from 'shared/assets/icons';
import { Icon } from 'shared/components/ui/icon';
import {
  PopupMenu,
  PopupMenuButton,
  PopupMenuInput,
  PopupMenuList,
  PopupMenuListItem,
  PopupMenuNoResults,
} from 'shared/components/ui/popup-menu';
import { useFocusChildrenWithArrows } from 'shared/hooks/use-focus-children-with-arrows';
import { ID } from 'shared/types/id';

import * as Styled from './select-tags-context-menu.style';

export type SelectTagsContextMenuProps = Omit<
  React.ComponentProps<typeof PopupMenu>,
  'onOutside' | 'position' | 'children'
> & {
  onTag: (id: ID) => void;
  activeTags: ID[];
  onClose: () => void;
};

export const SelectTagsContextMenu: React.FC<SelectTagsContextMenuProps> = ({
  onClose,
  onTag,
  activeTags,
  ...popupMenuProps
}) => {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const tags = useSortedTags();
  const [searchValue, setSearchValue] = useState('');

  const onChangeInput = (e: React.ChangeEvent<HTMLInputElement>) =>
    setSearchValue(e.target.value);

  const filteredTags = useMemo(
    () => getAutoCompleteSuggestions(searchValue, tags, ['name']),
    [searchValue, tags],
  );

  useFocusChildrenWithArrows(containerRef);

  return (
    <PopupMenu
      {...popupMenuProps}
      ref={containerRef}
      onOutside={onClose}
      position="bottom-end"
      initialFocus={inputRef}
    >
      <PopupMenuList hasBottomBorder>
        <PopupMenuListItem>
          <PopupMenuInput
            ref={inputRef}
            startIcon={Search}
            value={searchValue}
            placeholder={t('general.labels.search')}
            onChange={onChangeInput}
          />
        </PopupMenuListItem>
      </PopupMenuList>

      <PopupMenuList>
        {!filteredTags.length && !!searchValue && (
          <PopupMenuNoResults>
            {t('general.labels.no-results')}
          </PopupMenuNoResults>
        )}
        {filteredTags.map(({ id, name, color }) => {
          const _onTag = () => onTag(id);

          return (
            <PopupMenuListItem key={id}>
              <PopupMenuButton
                start={
                  <Styled.IconContainer $color={color}>
                    <Icon icon={Tag} />
                  </Styled.IconContainer>
                }
                end={
                  activeTags.includes(id) ? (
                    <Styled.CheckIconContainer>
                      <Icon icon={Check} />
                    </Styled.CheckIconContainer>
                  ) : undefined
                }
                onClick={_onTag}
              >
                {name}
              </PopupMenuButton>
            </PopupMenuListItem>
          );
        })}
      </PopupMenuList>
    </PopupMenu>
  );
};
